import { Suspense, lazy, useEffect } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

import { cn } from "@/lib/utils";

import type { Id } from "@convex/_generated/dataModel";

import type { BeadValueObject } from "../../../domain/praxis-beads";
import { useBeads } from "../../hooks/useBeads";
import { useRightPanelStore } from "../../store/rightPanelStore";
import { BeadCompactRow, BeadDetailDrawer } from "./BeadDetailDrawer";
import { TaskPanelHeader } from "./TaskPanelHeader";

const SpecTab = lazy(() =>
  import("./SpecTab").then((m) => ({ default: m.SpecTab })),
);

const ExecutionTab = lazy(() =>
  import("./ExecutionTab").then((m) => ({ default: m.ExecutionTab })),
);

interface Props {
  readonly className?: string;
  readonly threadId: Id<"threads">;
}

/**
 * The workflow workbench — a focused, centered overlay that comes forward
 * over a dimmed, blurred world (document-review mode). Reviewing a spec or a
 * blueprint is a lean-in task, so it gets a generous reading width rather than
 * fighting the world and chat for a cramped rail. The node detail surfaces
 * render inside this card (a right-hand side-rail at this width), never as a
 * fourth column. On phones it becomes a full-screen sheet.
 */
export function TaskPanel({ className, threadId }: Props) {
  const isOpen = useRightPanelStore((s) => s.isOpen);
  const close = useRightPanelStore((s) => s.close);
  const activeTab = useRightPanelStore((s) => s.activeTab);
  const setActiveTab = useRightPanelStore((s) => s.setActiveTab);
  const selectedBeadId = useRightPanelStore((s) => s.selectedBeadId);
  const selectBead = useRightPanelStore((s) => s.selectBead);
  const reduced = useReducedMotion() ?? false;

  const { beads } = useBeads();
  // biome-ignore lint/suspicious/noExplicitAny: convex rows
  const allBeads = beads as unknown as Array<BeadValueObject & { _id: string }>;
  // Gate the drawer on the store signal only — never on a lookup against a
  // possibly-lagging Convex snapshot. The drawer itself handles "bead not
  // found" when the lookup fails.
  const showDrawer = selectedBeadId !== null;
  const selectedBead = showDrawer
    ? allBeads.find((b) => b._id === selectedBeadId) ?? null
    : null;

  // Escape closes the workbench, matching the FloatingWindow contract.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  return (
    <AnimatePresence>
      {isOpen ? (
        <>
          {/* Scrim — dims + blurs the world and chat behind, click to close */}
          <motion.div
            key="workbench-scrim"
            initial={reduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => close()}
            aria-hidden="true"
            className="pointer-events-auto absolute inset-0 z-40 bg-home-ink/25 backdrop-blur-sm dark:bg-black/50"
          />

          {/* Centered workbench card */}
          <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center p-4 md:p-8">
            <motion.div
              key="workbench-card"
              initial={reduced ? false : { opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.98, y: 8 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              className={cn(
                "pointer-events-auto flex h-full max-h-[860px] w-full max-w-[960px] flex-col overflow-hidden rounded-[24px] border border-home-ink/10 bg-home-cream shadow-[0_40px_100px_-30px_rgba(33,31,28,0.55)] dark:border-white/10 dark:bg-neutral-950",
                "max-lg:h-full max-lg:max-h-none max-lg:max-w-none max-lg:rounded-none",
                className,
              )}
            >
              <TaskPanelHeader
                activeTab={activeTab}
                onTabChange={setActiveTab}
                hasSelectedBead={showDrawer}
                onBack={showDrawer ? () => selectBead(null) : undefined}
              />
              <div className="relative flex-1 overflow-hidden">
                {showDrawer ? (
                  <div className="flex h-full flex-col">
                    {selectedBead !== null ? (
                      <div className="max-lg:hidden">
                        <BeadCompactRow bead={selectedBead} />
                      </div>
                    ) : null}
                    <div className="flex-1 overflow-hidden">
                      <BeadDetailDrawer beadId={selectedBeadId as string} />
                    </div>
                  </div>
                ) : activeTab === "execution" ? (
                  <Suspense
                    fallback={
                      <div className="flex h-full items-center justify-center font-body text-[13px] text-home-ink/50 dark:text-white/50">
                        Loading flow…
                      </div>
                    }
                  >
                    <ExecutionTab threadId={threadId} />
                  </Suspense>
                ) : (
                  <Suspense
                    fallback={
                      <div className="flex h-full items-center justify-center font-body text-[13px] text-home-ink/50 dark:text-white/50">
                        Loading workflow…
                      </div>
                    }
                  >
                    <SpecTab threadId={threadId} />
                  </Suspense>
                )}
              </div>
            </motion.div>
          </div>
        </>
      ) : null}
    </AnimatePresence>
  );
}
